import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuProps } from 'antd';
import { Dropdown, Modal } from 'antd';
import { MoreOutlined, ExclamationCircleOutlined, EditOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import type { TUpdateTripTaskReq } from 'api/swagger';
import { useDeleteTripTask } from 'api/trip-task/use-delete-trip-task';
import { useMergeTripTasks } from 'api/trip-task/use-merge-trip-tasks';
import { useUpdateTripTask } from 'api/trip-task/use-update-trip-task';
import { UserRole, withRoleGuard } from 'shared/hocs/with-role-guard.tsx';
import type { TDropdownChangeInfo } from 'types/dropdown-change-info';
import { ETripTaskStatus } from 'types/trip-task-status';
import type { ETripTaskType } from 'types/trips';
import { TripEditTaskModal } from 'components/trips/trip-edit-task-modal';
import { canEditTripTask } from 'components/trips/trip-edit-task-modal/utils';
import { TripTaskMergeModal } from 'components/trips/trip-tasks/components/trip-task-merge-modal';
import { TripTaskStatusEditModal } from 'components/trips/trip-tasks/components/trip-task-status-edit-modal';
import type { ITripTaskItemControlProps } from './types';

export const TripCargoItemControlComponent = (props: ITripTaskItemControlProps) => {
  const { tripTask, tripStatus, mergeTasks } = props;
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();
  const { mutateAsync: deleteTripTask } = useDeleteTripTask();
  const { mutateAsync: updateTripTask, isLoading } = useUpdateTripTask(tripTask.tripId);
  const { mutateAsync: mergeTripTasks, isLoading: isMergePending } = useMergeTripTasks(tripTask.tripId);

  const [isActiveControl, setIsActiveControl] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);

  const deactivateWithTimeout = () => {
    setTimeout(() => setIsActiveControl(false), 300);
  };

  const confirm = () => {
    modal.confirm({
      title: t('common:confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('common:delete_confirm_text'),
      okText: t('common:delete'),
      cancelText: t('common:cancel'),
      onCancel: () => deactivateWithTimeout(),
      onOk: async () => {
        await deleteTripTask({
          tripId: tripTask.tripId,
          tripTaskId: tripTask.id,
        });
        deactivateWithTimeout();
      },
    });
  };

  const handleOpenStatusModal = () => {
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
  };

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    deactivateWithTimeout();
  };

  const handleOpenMergeModal = () => {
    setIsMergeModalOpen(true);
  };

  const handleCloseMergeModal = () => {
    setIsMergeModalOpen(false);
    deactivateWithTimeout();
  };

  const handleSubmitMerge = async (taskIds: number[]) => {
    await mergeTripTasks({ taskIds });
    handleCloseMergeModal();
  };

  const handleSubmitEdit = async (payload: TUpdateTripTaskReq) => {
    await updateTripTask({
      tripTaskId: tripTask.id,
      ...payload,
    });
    handleCloseEditModal();
  };

  const handleDropdownClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'delete') {
      confirm();
    } else if (key === 'change-status') {
      handleOpenStatusModal();
    } else if (key === 'edit') {
      handleOpenEditModal();
    } else if (key === 'merge') {
      handleOpenMergeModal();
    }
  };

  const handleDropdownOpenChange = (value: boolean, info: TDropdownChangeInfo) => {
    if (info.source === 'trigger') {
      setIsActiveControl(value);
    }
  };

  const canUpdateStatus = tripStatus === 'progress' && tripTask.status !== ETripTaskStatus.done;
  const canEdit = canEditTripTask(tripTask.type as ETripTaskType, tripTask.status as ETripTaskStatus, tripStatus);

  const menuItems: MenuProps['items'] = [
    ...(canEdit
      ? [
          {
            key: 'edit',
            label: t('trip:edit_trip_task'),
            icon: <EditOutlined />,
          },
        ]
      : []),
    ...(canUpdateStatus
      ? [
          {
            key: 'change-status',
            label: t('trip:edit_trip_task_status'),
            icon: <EditOutlined />,
          },
        ]
      : []),
    {
      key: 'merge',
      label: t('trip:merge_tasks'),
      icon: <LinkOutlined />,
      disabled: mergeTasks.length < 2,
    },
    {
      key: 'delete',
      label: t('common:delete'),
      icon: <DeleteOutlined />,
    },
  ];

  return (
    <>
      <Dropdown
        key='more'
        trigger={['click']}
        menu={{
          items: menuItems,
          onClick: handleDropdownClick,
        }}
        placement='bottomLeft'
        onOpenChange={handleDropdownOpenChange}>
        <MoreOutlined style={{ fontSize: 20 }} />
      </Dropdown>
      {isActiveControl ? contextHolder : null}
      <TripTaskStatusEditModal
        key={tripTask.status}
        open={isStatusModalOpen}
        tripTask={tripTask}
        onCancel={handleCloseStatusModal}
        onSuccess={handleCloseStatusModal}
      />
      <TripEditTaskModal
        open={isEditModalOpen}
        tripTask={tripTask}
        tripStatus={tripStatus}
        isPending={isLoading}
        onCancel={handleCloseEditModal}
        onSubmit={handleSubmitEdit}
      />
      <TripTaskMergeModal
        open={isMergeModalOpen}
        tasks={mergeTasks}
        isPending={isMergePending}
        onCancel={handleCloseMergeModal}
        onSubmit={handleSubmitMerge}
      />
    </>
  );
};

export const TripCargoItemControl = withRoleGuard(TripCargoItemControlComponent, [
  UserRole.admin,
  UserRole.assistant,
  UserRole.transport_manager,
]);
